import { EnumColorPreset, type EnumOption } from '@doss/prisma/types'
import { z } from 'zod'

const enumColorPresetSchema = z.enum(EnumColorPreset)

const OKLCH_CHANNELS_REGEX = /^(\d+(?:\.\d+)?) (\d+(?:\.\d+)?) (\d+(?:\.\d+)?)$/

// Custom color as OKLCH channel values "L C H" (no oklch() wrapper, e.g. "0.6 0.25 145"). The regex
// handles shape and non-negativity; the refine bounds L ≤ 1 and H ≤ 360 (chroma is unbounded).
export const oklchColorStringSchema = z.string().superRefine((value, ctx) => {
  const match = OKLCH_CHANNELS_REGEX.exec(value)
  if (!match) {
    ctx.addIssue({
      code: 'custom',
      message:
        'colorCustom must be three space-separated OKLCH channel values "L C H", e.g. "0.6 0.25 145"',
    })
    return
  }
  if (Number(match[1]) > 1) {
    ctx.addIssue({
      code: 'custom',
      message: 'OKLCH lightness (L) must be between 0 and 1',
    })
  }
  if (Number(match[3]) > 360) {
    ctx.addIssue({
      code: 'custom',
      message: 'OKLCH hue (H) must be between 0 and 360',
    })
  }
})

/** Labels are an option's natural key, compared exactly apart from surrounding space */
export const normalizeEnumLabel = (label: string): string => label.trim()

const enumOptionLabelSchema = z.string().trim().min(1, 'label must not be empty')

const enumOptionColorFields = {
  colorPreset: enumColorPresetSchema.nullish(),
  colorCustom: oklchColorStringSchema.nullish(),
}

const refineAtMostOneColor = (
  value: { colorPreset?: EnumColorPreset | null; colorCustom?: string | null },
  ctx: z.RefinementCtx,
): void => {
  const colorCount = (value.colorPreset != null ? 1 : 0) + (value.colorCustom != null ? 1 : 0)
  if (colorCount > 1) {
    ctx.addIssue({
      code: 'custom',
      message: 'An enum option may set at most one of colorPreset or colorCustom',
    })
  }
}

// Order is positional (the array index), not a field on the input
export const enumOptionInputSchema = z
  .object({
    label: enumOptionLabelSchema,
    ...enumOptionColorFields,
    iconId: z.string().trim().min(1).nullish(),
  })
  .strict()
  .superRefine(refineAtMostOneColor)
export type EnumOptionInput = z.infer<typeof enumOptionInputSchema>

export const enumOptionInputListSchema = z
  .array(enumOptionInputSchema)
  .superRefine((options, ctx) => {
    const seenLabels = new Set<string>()
    options.forEach((option, index) => {
      const key = normalizeEnumLabel(option.label)
      if (seenLabels.has(key)) {
        ctx.addIssue({
          code: 'custom',
          message: `Duplicate option label "${option.label}"`,
          path: [index, 'label'],
        })
      } else {
        seenLabels.add(key)
      }
    })
  })

export const parseEnumOptionInputList = (options: unknown): EnumOptionInput[] =>
  enumOptionInputListSchema.parse(options)

// Adapts the label-only `enumOptions: [String!]` shape to option inputs
export const enumOptionInputsFromLabels = (labels: string[]): EnumOptionInput[] =>
  labels.map((label) => ({ label }))

/**
 * Resolve the enum options to write on create: the rich `enumOptionInputs` are the source of truth
 * when provided (so an empty array means no options), otherwise fall back to the legacy labels.
 */
export const resolveCreateEnumOptions = (
  enumOptionInputs: EnumOptionInput[] | undefined,
  labels: string[] | null | undefined,
): EnumOptionInput[] | undefined => {
  if (enumOptionInputs !== undefined) {
    return enumOptionInputs
  }
  return labels && labels.length > 0 ? enumOptionInputsFromLabels(labels) : undefined
}

export type ExistingEnumOption = Pick<
  EnumOption,
  'id' | 'label' | 'colorPreset' | 'colorCustom' | 'iconId' | 'order'
>

export type EnumOptionReconciliation = {
  toCreate: Array<Pick<EnumOption, 'label' | 'colorPreset' | 'colorCustom' | 'iconId' | 'order'>>
  toUpdate: Array<Pick<EnumOption, 'id' | 'colorPreset' | 'colorCustom' | 'iconId' | 'order'>>
  toDelete: string[]
  // Ordered label set to write into the TableColumn.enumOptions mirror
  mirror: string[]
}

// Diffs the desired options against existing rows, keyed by normalized label: a match updates
// color/icon/order (row + id kept), a new label is created, an absent label is deleted
// (the caller gates deletes on in-use). Order is positional; input is assumed already validated
// via the list schema.
export const reconcileEnumOptions = (
  existing: ExistingEnumOption[],
  input: EnumOptionInput[],
): EnumOptionReconciliation => {
  const existingByLabel = new Map<string, ExistingEnumOption>()
  for (const option of existing) {
    existingByLabel.set(normalizeEnumLabel(option.label), option)
  }

  const toCreate: EnumOptionReconciliation['toCreate'] = []
  const toUpdate: EnumOptionReconciliation['toUpdate'] = []
  const mirror: string[] = []
  const matchedIds = new Set<string>()

  input.forEach((option, order) => {
    const colorPreset = option.colorPreset ?? null
    const colorCustom = option.colorCustom ?? null
    const iconId = option.iconId ?? null
    const match = existingByLabel.get(normalizeEnumLabel(option.label))
    if (match) {
      matchedIds.add(match.id)
      toUpdate.push({ id: match.id, colorPreset, colorCustom, iconId, order })
      // Keep the stored label (cells reference it) even if the input's surrounding space differs
      mirror.push(match.label)
    } else {
      toCreate.push({ label: option.label, colorPreset, colorCustom, iconId, order })
      mirror.push(option.label)
    }
  })

  const toDelete = existing
    .filter((option) => !matchedIds.has(option.id))
    .map((option) => option.id)

  return { toCreate, toUpdate, toDelete, mirror }
}
